/**
 * Returns a function, that, when invoked, will only be triggered at most once during
 * a given window of time.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Time window (in milliseconds).
 * @returns {Function}
 */
function throttle(fn, wait = 300) {
  let throttleTimeoutId = -1;
  let tick = false;

  return () => {
    clearTimeout(throttleTimeoutId);
    throttleTimeoutId = setTimeout(fn, wait);

    if (!tick) {
      fn.call();
      tick = true;
      setTimeout(() => {
        tick = false;
      }, wait);
    }
  };
}

if (!customElements.get('sticky-atc')) {
  customElements.whenDefined('product-form').then(() => {
    class StickyAtc extends customElements.get('product-form') {
      constructor() {
        super();

        this.section = this.closest('.section-main-product');
        this.mainProductForm = this.section.querySelector('.js-product');
        this.buyButtons = this.section.querySelector('buy-buttons');
        this.imageContainer = this.querySelector('.sticky-atc__image');
        this.variantContainer = this.querySelector('.sticky-atc__details__variant');
        this.variantTitle = this.querySelector('.sticky-atc__details__variant__title');
        this.variantTitle?.toggleAttribute('hidden', !this.section.querySelector('variant-picker'));

        this.isMonarchHybrid = this.dataset.monarchHybrid === 'true';
        this.currentVariantAvailable = this.dataset.currentVariantAvailable === 'true';
        this.variantChangeLink = this.querySelector('.sticky-atc__details__variant__change');
        this.monarchPrompt = this.querySelector('[data-monarch-prompt]');
        this.mainVariantIdInput = this.section.querySelector(
          'buy-buttons input[name="id"]'
        );
        this.mainAddButton = this.section.querySelector(
          'buy-buttons [name="add"]'
        );

        if (this.isMonarchHybrid) {
          this.initialiseMonarchPresentation();
          this.initMonarchPurchaseStateObserver();
        }

        if (this.isMonarchHybrid && this.variantChangeLink) {
          this.boundOnMonarchOptionsClick = this.onMonarchOptionsClick.bind(this);
          this.variantChangeLink.addEventListener('click', this.boundOnMonarchOptionsClick);
        }

        this.throttledOnScroll = throttle(StickyAtc.handleScroll.bind(this));
        window.addEventListener('scroll', this.throttledOnScroll);

        this.section.addEventListener('on:variant:change', this.onVariantChange.bind(this));

        if (!this.isMonarchHybrid) {
          this.section.addEventListener('on:media-gallery:change', this.updateImage.bind(this));
        }

        this.updateImage();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.throttledOnScroll);

        if (this.boundOnMonarchOptionsClick && this.variantChangeLink) {
          this.variantChangeLink.removeEventListener('click', this.boundOnMonarchOptionsClick);
        }

        if (this.monarchPurchaseObserver) {
          this.monarchPurchaseObserver.disconnect();
        }
      }

      /**
       * Tidies Monarch variant titles for the compact sticky bar.
       * Example:
       * "CroMo 4130 Rail · 262g / Turquoise Blue"
       * becomes:
       * "CroMo 4130 Rail · Turquoise Blue"
       * @param {string} title - Variant title.
       * @returns {string} - Compact title.
       */
      formatMonarchVariantTitle(title) {
        return (title || '')
          .replace(/\s*·\s*\d+(?:\.\d+)?\s*g\b/gi, '')
          .replace(/\bCroMo\s*4130\s*Rail\b/gi, 'CrMo')
          .replace(/\b4130\s*CrMo\s*Rail\b/gi, 'CrMo')
          .replace(/\bCrMo\s*4130\s*Rail\b/gi, 'CrMo')
          .replace(/\bTi-MX\s*Rail\b/gi, 'Ti-MX')
          .replace(/\s*\/\s*/g, ' · ')
          .replace(/\s{2,}/g, ' ')
          .trim();
      }

      /**
       * Applies the initial Monarch visual state on page load.
       */
      initialiseMonarchPresentation() {
        if (this.variantTitle && this.variantTitle.textContent.trim()) {
          this.variantTitle.textContent = this.formatMonarchVariantTitle(
            this.variantTitle.textContent
          );
        }

        const currentVariantId = this.dataset.currentVariant || '';

        if (!currentVariantId) {
          this.setMonarchPresentationState('empty');
        } else if (this.currentVariantAvailable) {
          this.setMonarchPresentationState('available');
        } else {
          this.setMonarchPresentationState('unavailable');
        }
      }

      /**
       * Updates the supporting copy and state hook for Monarch.
       * @param {'empty'|'available'|'unavailable'} state - Current selection state.
       */
      setMonarchPresentationState(state) {
        if (!this.isMonarchHybrid) return;

        this.dataset.monarchState = state;

        if (this.monarchPrompt) {
          this.monarchPrompt.hidden = state !== 'empty';
        }
      }

      /**
       * Uses the main product form as the source of truth for whether the
       * currently selected Monarch variant can actually be purchased.
       * This avoids relying on a stale/early variant.available value.
       * @param {string|number} [expectedVariantId=''] - Expected selected variant.
       * @returns {boolean} Whether the main product form is ready to add it.
       */
      isMainVariantPurchasable(expectedVariantId = '') {
        const variantId = this.mainVariantIdInput
          ? String(this.mainVariantIdInput.value || '')
          : '';

        if (!variantId) return false;

        if (
          expectedVariantId
          && String(expectedVariantId) !== variantId
        ) {
          return false;
        }

        if (!this.mainAddButton) {
          return this.currentVariantAvailable;
        }

        return !this.mainAddButton.disabled
          && this.mainAddButton.getAttribute('aria-disabled') !== 'true';
      }

      /**
       * Synchronises the sticky CTA to the actual main product form state.
       * The main form is canonical because it already knows whether Shopify
       * considers the selected variant purchasable.
       * @param {object|null} [variant=null] - Latest variant event payload.
       */
      syncMonarchPurchaseState(variant = null) {
        if (!this.isMonarchHybrid) return;

        const variantId = this.mainVariantIdInput
          ? String(this.mainVariantIdInput.value || '')
          : '';

        const purchasable = variantId
          ? this.isMainVariantPurchasable(variantId)
          : false;

        this.currentVariantAvailable = purchasable;
        this.dataset.currentVariantAvailable = purchasable ? 'true' : 'false';

        if (variantId) {
          this.dataset.currentVariant = variantId;
        }

        if (!variantId) {
          this.setMonarchPresentationState('empty');
        } else if (purchasable) {
          this.setMonarchPresentationState('available');
        } else {
          this.setMonarchPresentationState('unavailable');
        }

        this.updateAddToCartButton(
          variant || (variantId ? { id: variantId, available: purchasable } : null)
        );
      }

      /**
       * Variant-picker events can fire before Symmetry finishes updating the
       * main Add to Cart button. Sync again after that update has had a chance
       * to complete.
       * @param {object|null} variant - Latest variant event payload.
       */
      scheduleMonarchPurchaseSync(variant) {
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            this.syncMonarchPurchaseState(variant);
          });
        });

        window.setTimeout(() => {
          this.syncMonarchPurchaseState(variant);
        }, 80);
      }

      /**
       * Watches the real main Add to Cart button so the sticky CTA always
       * mirrors it, including inventory/availability state changes.
       */
      initMonarchPurchaseStateObserver() {
        if (!this.isMonarchHybrid) return;

        if (this.mainAddButton && 'MutationObserver' in window) {
          this.monarchPurchaseObserver = new MutationObserver(() => {
            this.syncMonarchPurchaseState();
          });

          this.monarchPurchaseObserver.observe(this.mainAddButton, {
            attributes: true,
            attributeFilter: ['disabled', 'aria-disabled'],
            childList: true,
            subtree: true,
            characterData: true
          });
        }

        window.setTimeout(() => {
          this.syncMonarchPurchaseState();
        }, 0);
      }

      /**
       * Scrolls directly back to the Monarch visual rail/colour picker.
       * Falls back to the native variant picker and then the product-info anchor.
       */
      scrollToMonarchOptions() {
        const target = this.section.querySelector('.monarch-picker')
          || this.section.querySelector('variant-picker')
          || this.section.querySelector('#product-info');

        if (target && target.offsetParent) {
          theme.scrollToRevealElement(target);
        }
      }

      /**
       * Handles the sticky variant "Change" link on Monarch.
       * @param {object} evt - Event object.
       */
      onMonarchOptionsClick(evt) {
        if (!this.isMonarchHybrid) return;

        evt.preventDefault();
        this.scrollToMonarchOptions();
      }

      /**
       * Handles submission of the product form.
       * @param {object} evt - Event object.
       */
      async handleSubmit(evt) {
        evt.preventDefault();

        const mainVariantIdInput = this.section.querySelector(
          'product-form buy-buttons input[name="id"]'
        );
        const variantId = mainVariantIdInput ? mainVariantIdInput.value : '';

        /*
         * Monarch hybrid behaviour:
         * - No complete variant yet -> return to the visual picker.
         * - Complete but unavailable variant -> return to the visual picker.
         * - Complete and available variant -> continue through Symmetry's normal ATC flow.
         */
        if (
          this.isMonarchHybrid
          && (
            !variantId
            || !this.isMainVariantPurchasable(variantId)
          )
        ) {
          this.scrollToMonarchOptions();
          return;
        }

        if (!variantId) {
          const target = this.section.querySelector('#product-info');
          if (target && target.offsetParent) {
            theme.scrollToRevealElement(target);
          }
          return;
        }

        // Validate main form
        const customFormValid = this.mainProductForm.validate();
        if (!customFormValid || !this.mainProductForm.form.reportValidity()) {
          if (!customFormValid) {
            if (this.isMonarchHybrid) {
              this.scrollToMonarchOptions();
            } else {
              theme.scrollToRevealElement(this.mainProductForm);
            }
          } else {
            const input = Array.from(this.mainProductForm.form.elements).find(
              (el) => !el.checkValidity()
            );
            setTimeout(() => theme.scrollToRevealElement(input), 100);
          }
          return;
        }

        // Copy data
        this.form.querySelectorAll('[data-copied]').forEach((el) => el.remove());
        const formData = new FormData(this.mainProductForm.form);
        for (const p of formData) {
          if (!this.form.querySelector(`[name="${p[0]}"]`)) {
            const input = document.createElement('input');
            input.name = p[0];
            input.value = p[1];
            input.hidden = true;
            input.setAttribute('data-copied', '');
            this.form.append(input);
          }
        }

        super.handleSubmit(evt);
      }

      /**
       * Determine visibility after scroll.
       */
      static handleScroll() {
        const topOffset = document.querySelector('.pageheader--sticky')
          ? document.querySelector('.section-header').offsetHeight
          : 0;

        this.classList.toggle(
          'sticky-atc--out',
          this.buyButtons.getBoundingClientRect().bottom > topOffset
        );

        document.body.classList.toggle(
          'scrolled-to-bottom',
          window.scrollY + window.innerHeight + 100 > document.body.scrollHeight
        );
      }

      /**
       * Handle a change in variant on the page.
       * @param {Event} evt - Variant change event dispatched by variant-picker.
       */
      onVariantChange(evt) {
        const idInput = this.form?.querySelector('[name="id"]');
        const variant = evt.detail.variant;

        if (variant) {
          this.variantTitle.textContent = this.isMonarchHybrid
            ? this.formatMonarchVariantTitle(variant.title)
            : variant.title;
          this.variantContainer.hidden = false;
          if (idInput) idInput.value = variant.id;
        } else {
          this.variantContainer.hidden = true;
          this.variantTitle.textContent = '';
          if (idInput) idInput.value = '';
        }

        if (this.isMonarchHybrid) {
          this.dataset.currentVariant = variant ? String(variant.id) : '';

          if (!variant) {
            this.currentVariantAvailable = false;
            this.dataset.currentVariantAvailable = 'false';
            this.setMonarchPresentationState('empty');
          }

          this.scheduleMonarchPurchaseSync(variant);
        }

        this.updateImage(variant);

        if (!this.isMonarchHybrid) {
          this.updateAddToCartButton(variant);
        }

        this.setErrorMsgState();
      }

      /**
       * Returns the dedicated side-on Monarch image for a complete variant.
       * @param {object} variant - Current Shopify variant.
       * @returns {string} Image URL.
       */
      getMonarchVariantImage(variant) {
        const fallback = this.dataset.monarchDefaultImage || '';

        if (!variant || !Array.isArray(variant.options)) {
          return fallback;
        }

        const railIndex = parseInt(this.dataset.monarchRailIndex, 10);
        const colourIndex = parseInt(this.dataset.monarchColourIndex, 10);

        if (
          Number.isNaN(railIndex)
          || Number.isNaN(colourIndex)
          || railIndex < 0
          || colourIndex < 0
        ) {
          return fallback;
        }

        const rail = String(variant.options[railIndex] || '').toLowerCase();
        const colour = String(variant.options[colourIndex] || '').toLowerCase();

        let railKey = '';
        let colourKey = '';

        if (rail.includes('ti-mx')) {
          railKey = 'timx';
        } else if (rail.includes('cromo') || rail.includes('crmo')) {
          railKey = 'crmo';
        }

        if (colour.includes('turquoise') || colour === 'blue') {
          colourKey = 'blue';
        } else if (colour.includes('black')) {
          colourKey = 'black';
        } else if (colour.includes('red')) {
          colourKey = 'red';
        } else if (colour.includes('mango') || colour.includes('orange')) {
          colourKey = 'orange';
        } else if (
          colour.includes('slate')
          || colour.includes('grey')
          || colour.includes('gray')
        ) {
          colourKey = 'grey';
        }

        if (!railKey || !colourKey) {
          return fallback;
        }

        const imageMap = {
          crmo: {
            blue: this.dataset.monarchImageCrmoBlue,
            black: this.dataset.monarchImageCrmoBlack,
            red: this.dataset.monarchImageCrmoRed,
            orange: this.dataset.monarchImageCrmoOrange,
            grey: this.dataset.monarchImageCrmoGrey
          },
          timx: {
            blue: this.dataset.monarchImageTimxBlue,
            black: this.dataset.monarchImageTimxBlack,
            red: this.dataset.monarchImageTimxRed,
            orange: this.dataset.monarchImageTimxOrange,
            grey: this.dataset.monarchImageTimxGrey
          }
        };

        return imageMap[railKey]?.[colourKey] || fallback;
      }

      /**
       * Sets the dedicated Monarch sticky thumbnail.
       * @param {string} url - Image URL.
       */
      setMonarchImage(url) {
        if (!this.imageContainer || !url) return;

        let image = this.imageContainer.querySelector('[data-monarch-sticky-image]');

        if (!image) {
          image = document.createElement('img');
          image.alt = '';
          image.width = 600;
          image.height = 450;
          image.loading = 'eager';
          image.decoding = 'async';
          image.setAttribute('data-monarch-sticky-image', '');
          this.imageContainer.replaceChildren(image);
        }

        if (image.getAttribute('src') !== url) {
          image.setAttribute('src', url);
        }
      }

      /**
       * Updates the sticky image.
       * Monarch uses the dedicated transparent side-on rail/colour assets.
       * Other products keep Symmetry's normal active-gallery thumbnail behaviour.
       * @param {object|null} variant - Current variant, when supplied.
       */
      updateImage(variant = null) {
        if (this.isMonarchHybrid) {
          if (variant) {
            this.setMonarchImage(this.getMonarchVariantImage(variant));
          } else if (!this.dataset.currentVariant) {
            this.setMonarchImage(this.dataset.monarchDefaultImage || '');
          }
          return;
        }

        const mainImage = this.section.querySelector(
          '.media-gallery .main-image .slider__item.is-active .product-media img'
        );

        if (mainImage) {
          this.imageContainer.innerHTML = mainImage.outerHTML;
        } else {
          this.imageContainer.textContent = '';
        }
      }

      /**
       * Updates the "Add to Cart" button label and status.
       * @param {object} variant - Current variant object.
       */
      updateAddToCartButton(variant) {
        if (!this.submitBtn) return;

        if (this.isMonarchHybrid) {
          this.submitBtn.removeAttribute('aria-disabled');
          this.submitBtn.disabled = false;

          const variantId = this.mainVariantIdInput
            ? String(this.mainVariantIdInput.value || '')
            : '';

          if (!variantId) {
            this.submitBtn.textContent = this.submitBtn.dataset.chooseOptionsText
              || 'SELECT OPTIONS';
            return;
          }

          if (!this.isMainVariantPurchasable(variantId)) {
            this.submitBtn.textContent = this.submitBtn.dataset.viewOptionsText
              || 'SELECT OPTIONS';
            return;
          }

          this.submitBtn.textContent = this.submitBtn.dataset.addToCartText;
          return;
        }

        const variantAvailable = variant && variant.available;
        const unavailableStr = variant
          ? theme.strings.noStock
          : this.submitBtn.dataset.unavailableText;

        this.submitBtn.textContent = variantAvailable
          ? this.submitBtn.dataset.addToCartText
          : unavailableStr;
      }
    }

    customElements.define('sticky-atc', StickyAtc);
  });
}
